import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Download, CheckCircle, XCircle, Clock, AlertTriangle, FileText, PenTool, RotateCcw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/* ─── Signature Pad Component ─── */
function SignaturePad({ onSignatureChange }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  const getPos = useCallback((e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  }, []);

  const startDraw = useCallback((e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
  }, [getPos]);

  const draw = useCallback((e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setHasSignature(true);
  }, [isDrawing, getPos]);

  const stopDraw = useCallback(() => {
    if (isDrawing) {
      setIsDrawing(false);
      const canvas = canvasRef.current;
      const data = canvas.toDataURL('image/png');
      onSignatureChange(hasSignature ? data : '');
    }
  }, [isDrawing, hasSignature, onSignatureChange]);

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onSignatureChange('');
    // Redraw guide line
    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = '#CBD5E1';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(20, canvas.height - 30);
    ctx.lineTo(canvas.width - 20, canvas.height - 30);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.strokeStyle = '#0F1D32';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = 500;
    canvas.height = 160;
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    // Guide line
    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = '#CBD5E1';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(20, canvas.height - 30);
    ctx.lineTo(canvas.width - 20, canvas.height - 30);
    ctx.stroke();
    // Reset for drawing
    ctx.setLineDash([]);
    ctx.strokeStyle = '#0F1D32';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  return (
    <div>
      <div className="relative border-2 border-dashed border-slate-300 rounded-lg overflow-hidden bg-white" data-testid="signature-canvas-container">
        <canvas
          ref={canvasRef}
          className="w-full cursor-crosshair touch-none"
          style={{ height: '160px' }}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={stopDraw}
          onMouseLeave={stopDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={stopDraw}
          data-testid="signature-canvas"
        />
        {!hasSignature && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-slate-300 text-sm font-medium">Draw your signature here</p>
          </div>
        )}
      </div>
      <div className="flex justify-end mt-2">
        <button onClick={clear} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors" data-testid="clear-signature-btn">
          <RotateCcw className="w-3 h-3" /> Clear
        </button>
      </div>
    </div>
  );
}

/* ─── Main Proposal Portal ─── */
export default function ProposalPortal() {
  const { token } = useParams();
  const [proposal, setProposal] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [signatureData, setSignatureData] = useState('');
  const [declineReason, setDeclineReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionDone, setActionDone] = useState('');
  const [selectedAddons, setSelectedAddons] = useState(new Set());
  const [proposalDataLoaded, setProposalDataLoaded] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/portal/${token}`)
      .then(r => { setProposal(r.data); setLoading(false); })
      .catch(err => { setError(err.response?.data?.detail || 'Proposal not found'); setLoading(false); });
  }, [token]);

  useEffect(() => {
    if (proposal && !proposalDataLoaded) {
      axios.get(`${API}/api/portal/${token}/proposal-html`)
        .then(r => {
          if (r.data.proposal_data) {
            setProposalDataLoaded(r.data.proposal_data);
            const addons = r.data.proposal_data.addon_items || [];
            setSelectedAddons(new Set(addons.map((_, i) => i)));
          }
        }).catch(() => {});
    }
  }, [proposal, token, proposalDataLoaded]);

  const handleDownloadPDF = () => {
    window.open(`${API}/api/portal/${token}/pdf`, '_blank');
  };

  const handleAccept = async () => {
    if (!signerName.trim() || !signatureData) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/portal/${token}/accept`, {
        signer_name: signerName,
        signature_data: signatureData,
      });
      setActionDone('accepted');
      setAcceptOpen(false);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to submit');
    }
    setSubmitting(false);
  };

  const handleDecline = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/portal/${token}/decline`, { reason: declineReason });
      setActionDone('declined');
      setDeclineOpen(false);
    } catch { }
    setSubmitting(false);
  };

  if (loading) return (
    <div className="min-h-screen bg-[#F8F6F3] flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 rounded bg-[#0F1D32] animate-pulse mx-auto mb-3" />
        <p className="text-slate-400 font-[Manrope]">Loading your proposal...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#F8F6F3] flex items-center justify-center p-4">
      <Card className="max-w-md w-full text-center p-8 border-amber-200">
        <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-[#0F1D32] mb-2 font-[Cormorant_Garamond] italic">Unable to Load Proposal</h2>
        <p className="text-slate-500 text-sm">{error}</p>
        <p className="text-xs text-slate-400 mt-4">Please contact HDH Pools & Spas for assistance.</p>
      </Card>
    </div>
  );

  if (actionDone) return (
    <div className="min-h-screen bg-[#F8F6F3] flex items-center justify-center p-4">
      <Card className="max-w-md w-full text-center p-8">
        {actionDone === 'accepted' ? (
          <>
            <div className="w-20 h-20 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-500" />
            </div>
            <h2 className="text-2xl font-bold text-[#0F1D32] mb-2 font-[Cormorant_Garamond] italic">Proposal Accepted!</h2>
            <p className="text-slate-500 text-sm leading-relaxed">Thank you for choosing HDH Pools & Spas. You'll receive a confirmation email shortly. Our team will be in touch within 24 hours to kick off your project.</p>
          </>
        ) : (
          <>
            <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-slate-400" />
            </div>
            <h2 className="text-2xl font-bold text-[#0F1D32] mb-2 font-[Cormorant_Garamond] italic">Proposal Declined</h2>
            <p className="text-slate-500 text-sm leading-relaxed">We understand. If you change your mind or have questions, please don't hesitate to reach out.</p>
          </>
        )}
      </Card>
    </div>
  );

  if (proposal.status === 'signed') return (
    <div className="min-h-screen bg-[#F8F6F3] flex items-center justify-center p-4">
      <Card className="max-w-md w-full text-center p-8">
        <div className="w-20 h-20 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-emerald-500" />
        </div>
        <h2 className="text-2xl font-bold text-[#0F1D32] mb-2 font-[Cormorant_Garamond] italic">Proposal Signed</h2>
        <p className="text-slate-500 text-sm">This proposal was signed{proposal.signer_name ? ` by ${proposal.signer_name}` : ''}{proposal.signed_at ? ` on ${new Date(proposal.signed_at).toLocaleDateString()}` : ''}.</p>
        <p className="text-xs text-slate-400 mt-3">Your project is underway!</p>
      </Card>
    </div>
  );

  const p = proposal;
  const hasSpa = (p.line_items || []).some(i => i.name?.toLowerCase().includes('spa'));

  // Interactive add-on selection
  const pd = proposalDataLoaded;
  const baseItems = pd?.base_items || [];
  const addonItems = pd?.addon_items || [];
  const optionItems = pd?.option_items || [];
  const allOptions = [...addonItems, ...optionItems]; // Combined for toggle UI
  const totals = pd?.proposal?.totals || {};

  // Calculate live total based on selections — match internal preview formula exactly
  const rawBaseSell = totals.base_sell || baseItems.reduce((s, i) => s + (i.price || 0), 0);
  const rawAddonSell = totals.addon_sell || addonItems.reduce((s, i) => s + (i.price || 0), 0);
  const rawTotal = rawBaseSell + rawAddonSell;
  const overheadMult = totals.subtotal_before_discount && rawTotal > 0 ? totals.subtotal_before_discount / rawTotal : 1;
  const basePriceDisplay = Math.round(rawBaseSell * overheadMult);
  const discountAmount = Math.round(totals.discount_amount || 0);
  const discountLabel = pd?.bid?.discount_label || 'Discount';

  // Selected options total
  const selectedOptionsTotal = allOptions.reduce((s, item, idx) => {
    if (selectedAddons.has(idx)) {
      return s + Math.round((item.price || 0) * overheadMult);
    }
    return s;
  }, 0);
  const liveTotal = Math.round(basePriceDisplay + selectedOptionsTotal - discountAmount);

  const toggleAddon = (idx) => {
    setSelectedAddons(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  if (!pd) return (
    <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center">
      <div className="w-10 h-10 rounded bg-[#0F1D32] animate-pulse" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="proposal-portal">
      {/* Sticky nav */}
      <div className="sticky top-0 z-50 bg-[#0F1D32] border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-semibold tracking-[0.2em] text-[#F59E0B] uppercase">HDH Pools & Spas</p>
            <p className="text-white font-semibold text-sm">Proposal for {p.customer_name}</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-[9px] text-white/40 uppercase">Your Total</p>
              <p className="text-lg font-bold text-white font-mono">${liveTotal.toLocaleString()}</p>
            </div>
            <Button variant="outline" onClick={handleDownloadPDF} className="h-9 border-white/20 text-white hover:bg-white/10 text-xs" data-testid="portal-download-pdf">
              <Download className="w-3.5 h-3.5 mr-1.5" /> PDF
            </Button>
            <Button className="h-9 bg-[#F59E0B] hover:bg-[#D97706] text-[#0F1D32] px-5 text-sm font-bold rounded-sm" onClick={() => setAcceptOpen(true)} data-testid="portal-accept-btn">
              <PenTool className="w-3.5 h-3.5 mr-1.5" /> Sign & Accept
            </Button>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="bg-[#0F1D32] pt-8 pb-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-[11px] font-semibold tracking-[0.25em] text-[#F59E0B] uppercase">HDH Pools & Spas</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mt-2 font-[Cormorant_Garamond] italic">Custom Pool{hasSpa ? ' & Spa' : ''} Proposal</h1>
          <p className="text-white/50 text-sm mt-3">Prepared for <strong className="text-white">{p.customer_name}</strong></p>
          <p className="text-white/30 text-xs mt-1">{p.address}{p.city ? `, ${p.city}` : ''}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 space-y-8">
        {/* Design Preview Images (only if images exist) */}
        {(() => {
          const imgs = pd?.design_images || [];
          const slotOrder = ['concept_plan', 'daytime_render', 'evening_render'];
          const ordered = slotOrder.map(s => imgs.find(i => i.slot === s)).filter(Boolean);
          const introSection = (pd?.content_sections || []).find(s => s.key === 'design_preview_intro' && s.enabled !== false);
          if (ordered.length === 0) return null;
          return (
            <Card className="border-slate-200">
              <CardContent className="p-6">
                {introSection && (
                  <>
                    <p className="text-[10px] font-semibold tracking-[0.2em] text-[#F59E0B] uppercase mb-1">{introSection.title?.toUpperCase()}</p>
                    <h2 className="text-xl font-bold text-[#0F1D32] font-[Cormorant_Garamond] italic mb-3">{introSection.title}</h2>
                    <p className="text-sm text-slate-600 leading-relaxed mb-6">{introSection.body}</p>
                  </>
                )}
                <div className="space-y-6">
                  {ordered.map(img => (
                    <div key={img.slot}>
                      <img src={img.data_uri} alt={img.caption || img.label} className="w-full rounded-lg border border-slate-200" />
                      <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider text-center mt-2">{img.caption || img.label}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* Design Notes (only if images exist and notes are not empty) */}
        {(pd?.design_images || []).length > 0 && (pd?.design_notes || '').trim() && (
          <Card className="border-slate-200">
            <CardContent className="p-6">
              <p className="text-[10px] font-semibold tracking-[0.2em] text-[#F59E0B] uppercase mb-1">DESIGN NOTES</p>
              <h2 className="text-xl font-bold text-[#0F1D32] font-[Cormorant_Garamond] italic mb-3">Project Design Details</h2>
              {(pd.design_notes || '').split('\n').filter(p => p.trim()).map((p, i) => (
                <p key={i} className="text-sm text-slate-600 leading-relaxed mb-2">{p.trim()}</p>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Content sections (excluding Design Preview Intro which is shown above with images) */}
        {(pd?.content_sections || []).filter(s => s.enabled !== false && s.key !== 'design_preview_intro').map(s => (
          <Card key={s.key} className="border-slate-200">
            <CardContent className="p-6">
              <p className="text-[10px] font-semibold tracking-[0.2em] text-[#F59E0B] uppercase mb-1">{s.title?.toUpperCase()}</p>
              <h2 className="text-xl font-bold text-[#0F1D32] font-[Cormorant_Garamond] italic mb-3">{s.title}</h2>
              {(s.body || '').split('\n').filter(p => p.trim()).map((p, i) => (
                <p key={i} className="text-sm text-slate-600 leading-relaxed mb-2">{p.trim()}</p>
              ))}
            </CardContent>
          </Card>
        ))}

        {/* If no design images, show Design Preview Intro with other sections (already filtered above) */}
        {(pd?.design_images || []).length === 0 && (pd?.content_sections || []).filter(s => s.key === 'design_preview_intro' && s.enabled !== false).map(s => (
          <Card key={s.key} className="border-slate-200">
            <CardContent className="p-6">
              <p className="text-[10px] font-semibold tracking-[0.2em] text-[#F59E0B] uppercase mb-1">{s.title?.toUpperCase()}</p>
              <h2 className="text-xl font-bold text-[#0F1D32] font-[Cormorant_Garamond] italic mb-3">{s.title}</h2>
              {(s.body || '').split('\n').filter(p => p.trim()).map((p, i) => (
                <p key={i} className="text-sm text-slate-600 leading-relaxed mb-2">{p.trim()}</p>
              ))}
            </CardContent>
          </Card>
        ))}

        {/* Base Pool Investment */}
        <div className="bg-[#0F1D32] rounded-lg p-8 text-center">
          <p className="text-[10px] font-semibold tracking-[0.3em] text-[#F59E0B] uppercase">Base Pool Investment</p>
          {discountAmount > 0 && (
            <>
              <p className="mt-3 text-2xl font-medium text-white/40 line-through">${basePriceDisplay.toLocaleString()}</p>
              <p className="mt-1 text-sm text-[#4ADE80] font-semibold">{discountLabel}: -${discountAmount.toLocaleString()}</p>
              <p className="mt-2 text-5xl font-bold text-white tracking-tight">${(basePriceDisplay - discountAmount).toLocaleString()}</p>
            </>
          )}
          {!discountAmount && (
            <p className="mt-3 text-5xl font-bold text-white tracking-tight">${basePriceDisplay.toLocaleString()}</p>
          )}
          <p className="mt-3 text-xs text-white/40">Complete pool project as designed — ready to build</p>
        </div>

        {/* What's Included */}
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <p className="text-[10px] font-semibold tracking-[0.2em] text-[#F59E0B] uppercase mb-1">Included With Every HDH Pool</p>
            <h2 className="text-xl font-bold text-[#0F1D32] font-[Cormorant_Garamond] italic mb-4">What Sets Us Apart</h2>
            <div className="space-y-3">
              <div className="p-4 bg-[#F8F6F3] rounded" style={{borderLeft:'3px solid #F59E0B'}}>
                <p className="text-sm font-bold text-[#0F1D32]">Premium Pentair Equipment — Standard</p>
                <p className="text-xs text-slate-500 mt-1">Variable-speed pump, filter, 400K BTU heater, automation with app control, salt chlorination.</p>
              </div>
              <div className="p-4 bg-[#F8F6F3] rounded" style={{borderLeft:'3px solid #F59E0B'}}>
                <p className="text-sm font-bold text-[#0F1D32]">Electrical & Gas Runs to Your House — Included</p>
                <p className="text-xs text-slate-500 mt-1">We run electric and gas from the equipment pad to your home. Most builders stop at the pad.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Interactive Add-On Options */}
        {allOptions.length > 0 && (
          <Card className="border-slate-200">
            <CardContent className="p-6">
              <p className="text-[10px] font-semibold tracking-[0.2em] text-[#F59E0B] uppercase mb-1">ADD-ON OPTIONS</p>
              <h2 className="text-xl font-bold text-[#0F1D32] font-[Cormorant_Garamond] italic mb-2">Customize Your Project</h2>
              <p className="text-xs text-slate-400 mb-4">Toggle on the upgrades you'd like to include. Your total updates automatically.</p>
              <div className="space-y-2">
                {allOptions.map((item, idx) => {
                  const isOn = selectedAddons.has(idx);
                  const price = Math.round((item.price || 0) * overheadMult);
                  return (
                    <div key={idx}
                      className={`flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-all ${isOn ? 'border-[#F59E0B] bg-amber-50/50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                      onClick={() => toggleAddon(idx)}
                      data-testid={`addon-toggle-${idx}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${isOn ? 'border-[#F59E0B] bg-[#F59E0B]' : 'border-slate-300'}`}>
                          {isOn && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                        </div>
                        <span className={`text-sm font-medium ${isOn ? 'text-[#0F1D32]' : 'text-slate-600'}`}>{item.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-bold font-mono ${isOn ? 'text-[#0F1D32]' : 'text-slate-500'}`}>+${price.toLocaleString()}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Live Total */}
        <div className="bg-[#0F1D32] rounded-lg p-6 text-center">
          <p className="text-[10px] font-semibold tracking-[0.3em] text-[#F59E0B] uppercase">Your Project Total</p>
          <p className="mt-2 text-4xl sm:text-5xl font-bold text-white tracking-tight">${liveTotal.toLocaleString()}</p>
          {selectedAddons.size > 0 && (
            <p className="mt-2 text-xs text-white/40">Base pool + {selectedAddons.size} add-on{selectedAddons.size > 1 ? 's' : ''} selected</p>
          )}
          {selectedAddons.size === 0 && (
            <p className="mt-2 text-xs text-white/40">Base pool only — add options above to customize</p>
          )}
        </div>

        {/* Site Conditions */}
        <div className="p-5 bg-[#FFFBEB] border border-[#FCD34D] rounded-lg">
          <p className="text-sm font-bold text-[#92400E] mb-2 font-[Cormorant_Garamond] italic">Site Conditions & Excavation</p>
          <p className="text-xs text-[#78350F] leading-relaxed">This proposal includes standard excavation for normal soil. If concealed conditions are encountered, we discuss options and pricing with you before proceeding. No surprise charges.</p>
        </div>

        {/* Warranty */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="border-slate-200 text-center p-4"><p className="text-2xl font-bold text-[#0F1D32]">10<span className="text-sm">yr</span></p><p className="text-[10px] text-slate-400 uppercase mt-1">Structural</p></Card>
          <Card className="border-slate-200 text-center p-4"><p className="text-2xl font-bold text-[#0F1D32]">1<span className="text-sm">yr</span></p><p className="text-[10px] text-slate-400 uppercase mt-1">Workmanship</p></Card>
          <Card className="border-slate-200 text-center p-4"><p className="text-2xl font-bold text-[#0F1D32]">MFR</p><p className="text-[10px] text-slate-400 uppercase mt-1">Equipment</p></Card>
        </div>

        {/* CTA */}
        <div className="bg-[#0F1D32] rounded-lg p-8 text-center">
          <p className="text-[10px] font-semibold tracking-[0.2em] text-[#F59E0B] uppercase mb-2">Ready to Start?</p>
          <p className="text-2xl font-bold text-white font-[Cormorant_Garamond] italic mb-4">Let's Build Your Dream Pool</p>
          <Button className="h-12 bg-[#F59E0B] hover:bg-[#D97706] text-[#0F1D32] px-10 text-base font-bold rounded-sm" onClick={() => setAcceptOpen(true)} data-testid="portal-accept-bottom-btn">
            <PenTool className="w-4 h-4 mr-2" /> Sign & Accept Proposal
          </Button>
        </div>

        <div className="text-center py-4">
          <button onClick={() => setDeclineOpen(true)} className="text-xs text-slate-400 hover:text-red-500 transition-colors" data-testid="portal-decline-btn">
            Not ready? Decline this proposal
          </button>
        </div>
      </div>

      {/* E-Signature Accept Dialog */}
      <Dialog open={acceptOpen} onOpenChange={setAcceptOpen}>
        <DialogContent className="max-w-lg" data-testid="accept-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-[#0F1D32] font-[Cormorant_Garamond] italic">Sign Your Proposal</DialogTitle>
            <p className="text-xs text-slate-500 mt-1">Review and sign below to accept this proposal for <strong className="text-[#0F1D32]">${liveTotal.toLocaleString()}</strong>.</p>
          </DialogHeader>
          <div className="space-y-5">
            <div className="p-4 bg-[#0F1D32] rounded-sm text-center">
              <p className="text-[10px] font-semibold tracking-[0.2em] text-[#F59E0B] uppercase">Total Investment</p>
              <p className="text-3xl font-bold text-white mt-1">${liveTotal.toLocaleString()}</p>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700">Full Legal Name *</Label>
              <Input value={signerName} onChange={e => setSignerName(e.target.value)} placeholder="Enter your full name" className="h-11 text-base mt-1" data-testid="signer-name-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700 mb-2 block">Signature *</Label>
              <SignaturePad onSignatureChange={setSignatureData} />
            </div>
            <div className="p-3 bg-[#F8F6F3] rounded-sm border border-slate-200">
              <p className="text-[11px] text-slate-500 leading-relaxed">
                By clicking "Sign & Accept" below, I agree to the scope of work, exclusions, payment schedule, and terms outlined in this proposal from HDH Pools & Spas. This constitutes a legally binding agreement.
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAcceptOpen(false)} className="rounded-sm">Cancel</Button>
            <Button
              className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0F1D32] font-bold rounded-sm px-6"
              onClick={handleAccept}
              disabled={!signerName.trim() || !signatureData || submitting}
              data-testid="confirm-accept-btn"
            >
              {submitting ? 'Processing...' : 'Sign & Accept'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decline Dialog */}
      <Dialog open={declineOpen} onOpenChange={setDeclineOpen}>
        <DialogContent className="max-w-md" data-testid="decline-dialog">
          <DialogHeader><DialogTitle className="text-[#0F1D32]">Decline Proposal</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">We're sorry to hear that. Could you let us know why?</p>
            <Textarea value={declineReason} onChange={e => setDeclineReason(e.target.value)} placeholder="Optional: pricing, timing, scope changes..." rows={3} data-testid="decline-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeclineOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDecline} disabled={submitting} data-testid="confirm-decline-btn">
              {submitting ? 'Processing...' : 'Decline Proposal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
